// THE PORTAL REPORT'S AXES (Dashboard and Reporting polish). PURE.
//
// A y axis shows three or four labelled values (0 and two or three nice ones), never a fitted-to-the-data maximum that makes a
// tall bar out of nothing. The top is the smallest "nice" value (1, 2 or 5 times a power of ten, or a clock-friendly number of
// seconds) that holds the data, tried with two and with three intervals, so a seven-lead chart reads 0 / 5 / 10 and not 0 / 5 / 10 / 15.
// A chart whose data is empty gets a small, honest axis (0 / 1 / 2), not a division by zero.

export interface Axis {
  /** The value at the very top of the plot. Every datum is <= top. */
  top: number;
  /** The labelled values, lowest first, always starting at 0 and ending at `top`. */
  ticks: number[];
}

const NICE = [1, 2, 5];

/** The smallest 1/2/5 x 10^k step that is >= `min`, never below `floor`. */
export function niceStep(min: number, floor = 1): number {
  const want = Math.max(min, floor);
  let power = 10 ** Math.floor(Math.log10(want));
  for (let guard = 0; guard < 12; guard += 1) {
    for (const n of NICE) {
      const step = n * power;
      if (step >= want - 1e-9) return step;
    }
    power *= 10;
  }
  return want;
}

function fromSteps(max: number, steps: readonly number[]): Axis {
  const data = Number.isFinite(max) && max > 0 ? max : 0;
  let best: Axis | null = null;
  for (const intervals of [2, 3]) {
    const step = steps.find((s) => s * intervals >= data - 1e-9) ?? steps[steps.length - 1]!;
    const top = step * intervals;
    if (!best || top < best.top) best = { top, ticks: Array.from({ length: intervals + 1 }, (_, i) => step * i) };
  }
  return best!;
}

/** A count axis: whole numbers, 1/2/5 steps. */
export function countAxis(max: number): Axis {
  const data = Number.isFinite(max) && max > 0 ? max : 0;
  const steps: number[] = [];
  for (let p = 1; steps.length < 40; p *= 10) for (const n of NICE) steps.push(n * p);
  return fromSteps(data, steps);
}

// 1 s to a day, in the steps a person reads a clock in.
const SECOND_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 28800, 43200, 86400, 172800, 604800];

/** A duration axis in seconds, on clock-friendly steps. */
export function secondsAxis(max: number): Axis {
  return fromSteps(Number.isFinite(max) && max > 0 ? max : 0, SECOND_STEPS);
}

/** m:ss for the speed axis ("0:30", "1:00", "12:05"); an hour or more is h:mm:ss. */
export function formatMss(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}

/** Where a value sits on the plot, as a percentage from the BOTTOM (0 to 100). */
export const percentUp = (value: number, axis: Axis): number => (axis.top <= 0 ? 0 : Math.min(100, Math.max(0, (value / axis.top) * 100)));

/** The three call-quality totals shown in the legend. They sum what the chart draws, point by point. */
export function qualityTotals(points: ReadonlyArray<{ booked: number; goodConversation: number; notAFit: number }>): { booked: number; goodConversation: number; notAFit: number } {
  return points.reduce((t, p) => ({ booked: t.booked + p.booked, goodConversation: t.goodConversation + p.goodConversation, notAFit: t.notAFit + p.notAFit }), { booked: 0, goodConversation: 0, notAFit: 0 });
}
